import type { CheckoutPlanId } from "./checkout-contract";
import type { PaidFeatureId } from "./paid-features";

export const PURCHASE_ACCESS_EVIDENCE_SCHEMA = "invoice-builder.purchase-access-evidence.v1";

export type PurchaseAccessEvidenceKind =
  | "checkout_completed"
  | "license_activated"
  | "entitlement_restored"
  | "paid_tool_accessed"
  | "download_accessed";

export interface PurchaseAccessEvidenceEvent {
  schema: typeof PURCHASE_ACCESS_EVIDENCE_SCHEMA;
  metadataPolicy: "no_document_contents";
  eventId: string;
  kind: PurchaseAccessEvidenceKind;
  provider: string;
  providerPurchaseId: string;
  plan: CheckoutPlanId;
  installId: string;
  occurredAt: string;
  featureId?: PaidFeatureId;
  providerEventId?: string;
  providerPayerId?: string;
  providerOrderId?: string;
  providerCaptureId?: string;
  buyerEmailHash?: string;
  accessIpHash?: string;
  userAgentHash?: string;
}

export type PurchaseAccessEvidenceRejectCode =
  | "invalid_shape"
  | "invalid_schema"
  | "metadata_policy_not_safe"
  | "document_content_present"
  | "invalid_event_id"
  | "invalid_kind"
  | "invalid_provider"
  | "invalid_provider_purchase_id"
  | "invalid_plan"
  | "invalid_install_id"
  | "invalid_occurred_at"
  | "invalid_feature_id"
  | "invalid_optional_provider_reference"
  | "invalid_hash";

export interface PurchaseAccessEvidenceReject {
  code: PurchaseAccessEvidenceRejectCode;
  path: string;
  detail?: string;
}

export type PurchaseAccessEvidenceResult =
  | {
      ok: true;
      event: PurchaseAccessEvidenceEvent;
    }
  | {
      ok: false;
      reasons: PurchaseAccessEvidenceReject[];
    };

const ACCESS_EVENT_KINDS = new Set<PurchaseAccessEvidenceKind>([
  "checkout_completed",
  "license_activated",
  "entitlement_restored",
  "paid_tool_accessed",
  "download_accessed",
]);

const PAID_FEATURE_IDS = new Set<PaidFeatureId>([
  "bulkOutput",
  "premiumDesigns",
  "advancedEditor",
  "multipleBrandKits",
  "brandKitStyling",
  "unlimitedBusinessTemplates",
  "designPacks",
]);

const DOCUMENT_CONTENT_KEYS = new Set([
  "seller",
  "sellername",
  "selleraddress",
  "customer",
  "customername",
  "customeraddress",
  "buyer",
  "buyername",
  "lineitem",
  "lineitems",
  "item",
  "items",
  "description",
  "invoice",
  "invoicenumber",
  "invoicebody",
  "document",
  "documentcontent",
  "documentcontents",
  "spreadsheet",
  "spreadsheetrow",
  "spreadsheetrows",
  "csv",
  "xlsx",
  "pdf",
  "xml",
  "zip",
  "brandkitasset",
  "templatejson",
]);

const RAW_PERSONAL_ADDRESS_KEYS = new Set([
  "buyeremail",
  "payeremail",
  "customeremail",
  "email",
  "ip",
  "ipaddress",
  "accessip",
  "useragent",
]);

const SAFE_ID_PATTERN = /^[A-Za-z0-9._:-]{3,160}$/;
const SAFE_PROVIDER_PATTERN = /^[a-z][a-z0-9_-]{1,48}$/;
const HASH_PATTERN = /^(sha256:)?[a-f0-9]{32,128}$/i;
const INSTALL_ID_PATTERN = /^install_[A-Za-z0-9._:-]{8,107}$/;

export function validatePurchaseAccessEvidenceEvent(
  payload: unknown,
): PurchaseAccessEvidenceResult {
  const reasons: PurchaseAccessEvidenceReject[] = [];

  if (!isRecord(payload)) {
    return { ok: false, reasons: [{ code: "invalid_shape", path: "$" }] };
  }

  collectUnsafePayloadReasons(payload, "$", reasons);

  if (payload.schema !== PURCHASE_ACCESS_EVIDENCE_SCHEMA) {
    reasons.push({ code: "invalid_schema", path: "$.schema" });
  }

  if (payload.metadataPolicy !== "no_document_contents") {
    reasons.push({ code: "metadata_policy_not_safe", path: "$.metadataPolicy" });
  }

  if (!isSafeId(payload.eventId)) {
    reasons.push({ code: "invalid_event_id", path: "$.eventId" });
  }

  if (!ACCESS_EVENT_KINDS.has(payload.kind as PurchaseAccessEvidenceKind)) {
    reasons.push({ code: "invalid_kind", path: "$.kind" });
  }

  if (typeof payload.provider !== "string" || !SAFE_PROVIDER_PATTERN.test(payload.provider)) {
    reasons.push({ code: "invalid_provider", path: "$.provider" });
  }

  if (!isSafeId(payload.providerPurchaseId)) {
    reasons.push({ code: "invalid_provider_purchase_id", path: "$.providerPurchaseId" });
  }

  if (payload.plan !== "monthly" && payload.plan !== "lifetime") {
    reasons.push({ code: "invalid_plan", path: "$.plan" });
  }

  if (typeof payload.installId !== "string" || !INSTALL_ID_PATTERN.test(payload.installId)) {
    reasons.push({ code: "invalid_install_id", path: "$.installId" });
  }

  if (typeof payload.occurredAt !== "string" || Number.isNaN(Date.parse(payload.occurredAt))) {
    reasons.push({ code: "invalid_occurred_at", path: "$.occurredAt" });
  }

  if (
    payload.featureId !== undefined &&
    !PAID_FEATURE_IDS.has(payload.featureId as PaidFeatureId)
  ) {
    reasons.push({ code: "invalid_feature_id", path: "$.featureId" });
  }

  for (const key of [
    "providerEventId",
    "providerPayerId",
    "providerOrderId",
    "providerCaptureId",
  ] as const) {
    if (payload[key] !== undefined && !isSafeId(payload[key])) {
      reasons.push({
        code: "invalid_optional_provider_reference",
        path: `$.${key}`,
      });
    }
  }

  for (const key of ["buyerEmailHash", "accessIpHash", "userAgentHash"] as const) {
    if (payload[key] !== undefined && !isSafeHash(payload[key])) {
      reasons.push({ code: "invalid_hash", path: `$.${key}` });
    }
  }

  if (reasons.length > 0) {
    return { ok: false, reasons: uniqueReasons(reasons) };
  }

  return {
    ok: true,
    event: payload as unknown as PurchaseAccessEvidenceEvent,
  };
}

function collectUnsafePayloadReasons(
  value: unknown,
  path: string,
  reasons: PurchaseAccessEvidenceReject[],
): void {
  if (Array.isArray(value)) {
    value.forEach((item, index) => collectUnsafePayloadReasons(item, `${path}[${index}]`, reasons));
    return;
  }

  if (!isRecord(value)) return;

  for (const [key, child] of Object.entries(value)) {
    const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, "");
    if (DOCUMENT_CONTENT_KEYS.has(normalizedKey)) {
      reasons.push({ code: "document_content_present", path: `${path}.${key}` });
    }
    if (RAW_PERSONAL_ADDRESS_KEYS.has(normalizedKey)) {
      reasons.push({
        code: "document_content_present",
        path: `${path}.${key}`,
        detail: "store provider-owned or hashed access identifiers only",
      });
    }
    collectUnsafePayloadReasons(child, `${path}.${key}`, reasons);
  }
}

function isSafeId(value: unknown): value is string {
  return typeof value === "string" && SAFE_ID_PATTERN.test(value);
}

function isSafeHash(value: unknown): value is string {
  return typeof value === "string" && HASH_PATTERN.test(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function uniqueReasons(reasons: PurchaseAccessEvidenceReject[]): PurchaseAccessEvidenceReject[] {
  const seen = new Set<string>();
  return reasons.filter((reason) => {
    const key = `${reason.code}:${reason.path}:${reason.detail ?? ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
